import clsx from 'clsx'
import type { CSSProperties, PropsWithChildren } from 'react'
import React from 'react'
import type { ProgressSize, ProgressVariantProps } from './Progress'
import { colors } from './Progress'
import { useProgress } from './ProgressContext'

type IconSizes = {
  [key in ProgressSize]: {
    animateIndeterminate: string
    size: number
    strokeDasharray: string
    thickness: number
  }
}

const sizes: IconSizes = {
  large: {
    animateIndeterminate: 'animate-circular-indeterminate-large',
    size: 44,
    strokeDasharray: '80px, 200px',
    thickness: 3.6,
  },
  medium: {
    animateIndeterminate: 'animate-circular-indeterminate-medium',
    size: 24,
    strokeDasharray: '44px, 109px',
    thickness: 2.0,
  },
  small: {
    animateIndeterminate: 'animate-circular-indeterminate-small',
    size: 16,
    strokeDasharray: '29px, 73px',
    thickness: 1.3,
  },
}

function Circular({
  'aria-label': label,
  'aria-labelledby': labelledBy,
  'aria-valuemax': max,
  'aria-valuemin': min,
  'aria-valuenow': now,
  'aria-valuetext': valueText,
  children,
  color,
  id,
  size = 'large',
  value,
}: PropsWithChildren<ProgressVariantProps>): JSX.Element {
  const [descriptionId] = useProgress()

  const iconSize = sizes[size].size
  const iconThickness = sizes[size].thickness
  const style: CSSProperties = {}

  if (value === undefined) {
    style.strokeDasharray = sizes[size].strokeDasharray
    style.strokeDashoffset = '0px'
  } else {
    const circumference = 2 * Math.PI * ((iconSize - iconThickness) / 2)

    style.strokeDasharray = circumference.toFixed(3)
    style.strokeDashoffset =
      value === 0
        ? circumference.toFixed(3)
        : `${(((100 - value) / 100) * circumference).toFixed(3)}px`
  }

  let nameProps: Pick<
    ProgressVariantProps,
    'aria-label' | 'aria-labelledby'
  > = {
    'aria-labelledby': descriptionId,
  }

  if (labelledBy) {
    nameProps = {
      'aria-labelledby': labelledBy,
    }
  } else if (label) {
    nameProps = {
      'aria-label': label,
    }
  }

  return (
    <div className="inline-flex flex-col items-center justify-center">
      <div
        {...nameProps}
        aria-valuemax={max}
        aria-valuemin={min}
        aria-valuenow={now}
        aria-valuetext={valueText}
        className={clsx(value === undefined ? 'animate-spin-1.4' : null)}
        id={id}
        role="progressbar"
        style={{
          height: iconSize,
          transform: value === undefined ? undefined : 'rotate(-90deg)',
          width: iconSize,
        }}
      >
        <svg
          className={clsx('block overflow-hidden', colors[color].bar.circular)}
          viewBox={`${iconSize / 2} ${iconSize / 2} ${iconSize} ${iconSize}`}
        >
          <circle
            className={clsx(
              'stroke-current',
              value === undefined
                ? sizes[size].animateIndeterminate
                : 'duration-200 ease-in transition-stroke-dashoffset',
            )}
            cx={iconSize}
            cy={iconSize}
            fill="none"
            r={(iconSize - iconThickness) / 2}
            strokeWidth={iconThickness}
            style={style}
          />
        </svg>
      </div>
      {children}
    </div>
  )
}

export default Circular
